/**
 * MonthlyIncomePage — Month-by-month retirement income calendar.
 *
 * Route: /app/monthly-income
 *
 * Shows how the optimised annual drawdown blend is distributed across 12
 * months with a realistic UK payment schedule (state pension monthly, SIPP
 * quarterly, ISA quarterly, PCLS/GIA annually in April, etc.).
 */
import { useEffect, useState } from 'react'
import {
  CalendarDays, TrendingDown, Banknote, PiggyBank, RefreshCw, ChevronDown, ChevronUp,
} from 'lucide-react'
import { clsx } from 'clsx'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import { api } from '@/lib/api'
import IncomeTabs from '@/components/ui/IncomeTabs'

// ── Types ────────────────────────────────────────────────────────────────────

interface MonthSources {
  state_pension_gbp: number
  db_pension_gbp: number
  sipp_basic_rate_gbp: number
  sipp_higher_rate_gbp: number
  pcls_tax_free_gbp: number
  isa_gbp: number
  gia_cgt_free_gbp: number
  extra_gia_gbp: number
}

interface MonthData {
  month: number
  name: string
  gross_gbp: number
  net_gbp: number
  tax_gbp: number
  sources: MonthSources
}

interface Totals {
  annual_gross_gbp: number
  annual_net_gbp: number
  annual_tax_gbp: number
  average_monthly_net_gbp: number
}

interface CalendarResponse {
  retirement_age: number
  year_offset: number
  months: MonthData[]
  totals: Totals
  notes: string[]
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  `£${Math.round(n).toLocaleString('en-GB')}`

const fmtCompact = (n: number) => {
  if (n >= 1_000_000) return `£${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `£${(n / 1_000).toFixed(0)}k`
  return `£${Math.round(n)}`
}

// Colour palette for stacked bar chart — one per source
const SOURCE_COLOURS: Record<keyof MonthSources, string> = {
  state_pension_gbp:   '#34d399', // emerald
  db_pension_gbp:      '#6ee7b7', // lighter emerald
  sipp_basic_rate_gbp: '#60a5fa', // blue
  sipp_higher_rate_gbp:'#3b82f6', // deeper blue
  pcls_tax_free_gbp:   '#a78bfa', // purple
  isa_gbp:             '#fbbf24', // amber
  gia_cgt_free_gbp:    '#f87171', // rose
  extra_gia_gbp:       '#fb923c', // orange
}

const SOURCE_LABELS: Record<keyof MonthSources, string> = {
  state_pension_gbp:   'State Pension',
  db_pension_gbp:      'DB Pension',
  sipp_basic_rate_gbp: 'SIPP (basic rate)',
  sipp_higher_rate_gbp:'SIPP (higher rate)',
  pcls_tax_free_gbp:   'PCLS tax-free',
  isa_gbp:             'ISA',
  gia_cgt_free_gbp:    'GIA (CGT-free)',
  extra_gia_gbp:       'Extra GIA',
}

// Months with large one-off payments — highlight amber
const HEAVY_MONTHS = new Set([4, 9]) // April(4), September(9) — 1-indexed

// ── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({
  label, value, sub, icon: Icon, accent, hero,
}: {
  label: string
  value: string
  sub?: string
  icon: React.FC<{ className?: string }>
  accent?: string
  hero?: boolean
}) {
  return (
    <div className="card card-hover p-4 flex flex-col gap-1">
      <div className="panel-title flex items-center gap-2">
        <Icon className={clsx('h-4 w-4', accent ?? 'text-blue-light')} />
        {label}
      </div>
      <div className={hero ? 'stat-hero text-gradient' : 'stat-value'}>{value}</div>
      {sub && <div className="text-xs text-text-muted">{sub}</div>}
    </div>
  )
}

// ── Month card ───────────────────────────────────────────────────────────────

function MonthCard({ mo, maxGross }: { mo: MonthData; maxGross: number }) {
  const [open, setOpen] = useState(false)
  const isHeavy = HEAVY_MONTHS.has(mo.month)
  const barWidth = maxGross > 0 ? (mo.gross_gbp / maxGross) * 100 : 0

  return (
    <div
      className={clsx(
        'card card-hover p-4 cursor-pointer',
        isHeavy && 'border-amber/40 bg-amber/5 hover:bg-amber/10',
      )}
      onClick={() => setOpen(v => !v)}
    >
      {/* Header row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span
            className={clsx(
              'h-2.5 w-2.5 rounded-full',
              isHeavy ? 'bg-amber' : 'bg-blue-light',
            )}
          />
          <span className="text-sm font-medium text-text-secondary">{mo.name}</span>
        </div>
        {open ? (
          <ChevronUp className="h-4 w-4 text-text-muted" />
        ) : (
          <ChevronDown className="h-4 w-4 text-text-muted" />
        )}
      </div>

      {/* Net income — big number */}
      <div className="stat-value">{fmt(mo.net_gbp)}</div>
      <div className="text-xs text-text-muted">
        gross {fmtCompact(mo.gross_gbp)} · tax {fmtCompact(mo.tax_gbp)}
      </div>

      {/* Mini bar */}
      <div className="mt-3 h-1.5 rounded-full bg-surface-hover">
        <div
          className={clsx(
            'h-1.5 rounded-full transition-all',
            isHeavy ? 'bg-amber' : 'bg-blue-light',
          )}
          style={{ width: `${barWidth}%` }}
        />
      </div>

      {/* Expanded source breakdown */}
      {open && (
        <div className="mt-3 space-y-1 text-xs">
          {(Object.entries(mo.sources) as [keyof MonthSources, number][])
            .filter(([, v]) => v > 0)
            .map(([key, val]) => (
              <div key={key} className="flex justify-between text-text-secondary">
                <span className="flex items-center gap-1.5">
                  <span
                    className="inline-block h-1.5 w-1.5 rounded-full"
                    style={{ background: SOURCE_COLOURS[key] }}
                  />
                  {SOURCE_LABELS[key]}
                </span>
                <span className="text-text-primary">{fmt(val)}</span>
              </div>
            ))}
          {mo.tax_gbp > 0 && (
            <div className="flex justify-between text-rose-400 border-t border-border pt-1 mt-1">
              <span>Tax</span>
              <span>−{fmt(mo.tax_gbp)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function MonthlyIncomePage() {
  const [retirementAge, setRetirementAge] = useState(60)
  const [yearOffset, setYearOffset] = useState(0)
  const [data, setData] = useState<CalendarResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchData = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<CalendarResponse>(
        `/api/income/monthly?retirement_age=${retirementAge}&year_offset=${yearOffset}`,
      )
      setData(res.data)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load monthly income data.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retirementAge, yearOffset])

  const maxGross = data
    ? Math.max(...data.months.map(m => m.gross_gbp))
    : 0

  // Chart data — one entry per month
  const chartData = (data?.months ?? []).map(mo => ({
    name: mo.name.slice(0, 3),
    ...mo.sources,
  })) ?? []

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">

      {/* ── Page header ── */}
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <CalendarDays className="h-6 w-6 text-blue-light" />
            Monthly Retirement Income Calendar
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Every £ of retirement income, month by month — built from the
            optimised drawdown blend across state pension, SIPP, ISA and GIA.
          </p>
        </div>
        <button
          type="button"
          onClick={fetchData}
          disabled={loading}
          className="btn-secondary"
        >
          <RefreshCw className={clsx('h-4 w-4', loading && 'animate-spin')} />
          Refresh
        </button>
      </div>

      <IncomeTabs />

      {/* ── Controls ── */}
      <div className="card p-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label htmlFor="retirement-age-slider" className="block text-xs text-text-secondary mb-1">
            Retirement age: <span className="text-text-primary font-medium">{retirementAge}</span>
          </label>
          <input
            id="retirement-age-slider"
            type="range" min={50} max={75} value={retirementAge}
            title={`Retirement age: ${retirementAge}`}
            onChange={e => setRetirementAge(Number(e.target.value))}
            className="w-full accent-blue-light"
          />
        </div>
        <div>
          <label htmlFor="year-offset-slider" className="block text-xs text-text-secondary mb-1">
            Year into retirement: <span className="text-text-primary font-medium">
              {yearOffset === 0 ? 'First year' : `Year ${yearOffset + 1}`}
            </span>
          </label>
          <input
            id="year-offset-slider"
            type="range" min={0} max={34} value={yearOffset}
            title={`Year ${yearOffset} into retirement`}
            onChange={e => setYearOffset(Number(e.target.value))}
            className="w-full accent-blue-light"
          />
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="card border-rose/30 bg-rose/10 p-4 text-sm text-rose-300">
          {error}
        </div>
      )}

      {/* ── KPI cards ── */}
      {data?.totals && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger-children">
          <KpiCard
            label="Annual Gross"
            value={fmt(data.totals.annual_gross_gbp)}
            sub="before tax"
            icon={Banknote}
            accent="text-blue-light"
          />
          <KpiCard
            label="Annual Net"
            value={fmt(data.totals.annual_net_gbp)}
            sub="take-home"
            icon={TrendingDown}
            accent="text-emerald"
          />
          <KpiCard
            label="Annual Tax"
            value={fmt(data.totals.annual_tax_gbp)}
            sub="income tax + CGT"
            icon={PiggyBank}
            accent="text-rose-light"
          />
          <KpiCard
            label="Avg Monthly Net"
            value={fmt(data.totals.average_monthly_net_gbp)}
            sub="per month take-home"
            icon={CalendarDays}
            accent="text-amber"
            hero
          />
        </div>
      )}

      {/* ── 12-month calendar grid ── */}
      {data && data.months.length > 0 && (
        <div>
          <h2 className="panel-title mb-3">Income by Month</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {data.months.map(mo => (
              <MonthCard key={mo.month} mo={mo} maxGross={maxGross} />
            ))}
          </div>
        </div>
      )}

      {/* ── Stacked bar chart ── */}
      {data && data.months.length > 0 && (
        <div className="card p-4">
          <h2 className="panel-title mb-4">Monthly Sources (Gross)</h2>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 12 }} />
              <YAxis
                tick={{ fill: '#9ca3af', fontSize: 11 }}
                tickFormatter={v => `£${(v / 1000).toFixed(0)}k`}
              />
              <Tooltip
                formatter={(value: number, name: string) => [
                  fmt(value),
                  SOURCE_LABELS[name as keyof MonthSources] ?? name,
                ]}
                contentStyle={{
                  background: '#1a1f2e',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8, color: 'rgb(var(--text-primary))'}}
              />
              <Legend
                formatter={name => SOURCE_LABELS[name as keyof MonthSources] ?? name}
                wrapperStyle={{ fontSize: 11, color: '#9ca3af' }}
              />
              {(Object.keys(SOURCE_COLOURS) as (keyof MonthSources)[]).map(key => (
                <Bar
                  key={key}
                  dataKey={key}
                  stackId="a"
                  fill={SOURCE_COLOURS[key]}
                  name={key}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Loading skeleton ── */}
      {loading && !data && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {Array.from({ length: 12 }).map((_, i) => (
            <div
              key={i}
              className="h-28 skeleton"
            />
          ))}
        </div>
      )}

      {/* ── Methodology footer ── */}
      {data?.notes && data.notes.length > 0 && (
        <div className="card p-4">
          <h3 className="panel-title mb-2">Payment schedule assumptions</h3>
          <ul className="space-y-1">
            {data.notes.map((note, i) => (
              <li key={i} className="text-xs text-text-muted flex gap-2">
                <span className="text-text-muted">·</span>
                {note}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
